import { Alert, CircularProgress } from '@mui/material';
import clsx from 'clsx';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MaterialTextField from 'src/components/material/textField';
import { PathRoutes } from 'src/const/Routes/PathRoutes';
import { useAppDispatch } from 'src/hooks/Redux/useAppDispatch';
import { useUserLoginLogout } from 'src/hooks/UserLoginLogout/UserLoginLogout';
import { fetchLoggedUser } from 'src/redux/slices/loggedUserSlice';
import { fetchUser } from 'src/redux/slices/userSlice';
import featureStyles from '../RegisterModalShared.module.scss';
import styles from './LoginType.module.scss';

interface LoginTypeProps {
  redirectUrl?: string;
  goToSignup: () => void;
  goToResetPassword: () => void;
}

export interface IFormInput {
  phoneNumber: string;
  password: string;
}

export const LoginType: FC<LoginTypeProps> = ({
  redirectUrl,
  goToSignup,
  goToResetPassword,
}) => {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { login, loading } = useUserLoginLogout();

  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [validationErrors, setValidationErrors] = useState<{
    phoneNumber?: string;
    newPassword?: string;
    general?: string;
  }>({});

  const validateInputs = () => {
    const errors: {
      phoneNumber?: string;
      newPassword?: string;
      general?: string;
    } = {};

    if (!phoneNumber) {
      errors.phoneNumber = 'Required';
    }

    if (!newPassword) {
      errors.newPassword = 'Required';
    } else if (newPassword.length < 8) {
      errors.newPassword = 'Password must be at least 8 characters long';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePhoneNumberChange = (value: string) => {
    setPhoneNumber(value);
    if (validationErrors.phoneNumber) {
      setValidationErrors((prev) => ({ ...prev, phoneNumber: undefined }));
    }
  };

  const handlePasswordChange = (value: string) => {
    setNewPassword(value);
    if (validationErrors.newPassword) {
      setValidationErrors((prev) => ({ ...prev, newPassword: undefined }));
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateInputs()) {
      console.error('Validation failed');
      return;
    }

    try {
      const result = await login({
        username: phoneNumber,
        password: newPassword,
      });

      if (result?.isSignedIn) {
        dispatch(fetchUser());
        dispatch(fetchLoggedUser());

        navigate(redirectUrl ?? PathRoutes.FeedPageRoute);
      } else {
        setValidationErrors((prev) => ({
          ...prev,
          general: 'Invalid phone number or password',
        }));
      }
    } catch (err) {
      console.error('Login failed:', err);
      setValidationErrors((prev) => ({
        ...prev,
        general: 'An error occurred. Please try again later.',
      }));
    }
  };

  return (
    <section className={featureStyles.content}>
      <h1 className={featureStyles.title}>Login</h1>

      <form onSubmit={handleSubmit} className={featureStyles.form} noValidate>
        {validationErrors.general && (
          <Alert severity="error">{validationErrors.general}</Alert>
        )}

        <span>
          <MaterialTextField
            id="phone-number"
            name="phoneNumber"
            label="Phone Number"
            value={phoneNumber}
            color="primary"
            variant="outlined"
            isPhoneNumber
            helperText={validationErrors.phoneNumber}
            error={!!validationErrors.phoneNumber}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handlePhoneNumberChange(event.target.value)
            }
          />
          <MaterialTextField
            id="new-password"
            name="newPassword"
            label="Password"
            value={newPassword}
            color="primary"
            variant="outlined"
            isPassword
            helperText={validationErrors.newPassword}
            error={!!validationErrors.newPassword}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handlePasswordChange(event.target.value)
            }
          />

          <button
            type="button"
            className={clsx(
              featureStyles.link,
              featureStyles['link__button'],
              styles.link
            )}
            onClick={goToResetPassword}
          >
            Forgot your password?
          </button>
        </span>

        <button
          type="submit"
          className={clsx(
            featureStyles.button,
            featureStyles['button--primary']
          )}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress sx={{ color: '#fff' }} size={28} thickness={4} />
          ) : (
            'Submit'
          )}
        </button>
      </form>

      <div className={featureStyles.footer}>
        Don't have an account?
        <button
          type="button"
          className={clsx(
            featureStyles.link,
            featureStyles['link__button'],
            featureStyles['link--primary']
          )}
          onClick={goToSignup}
        >
          Sign up
        </button>
      </div>
    </section>
  );
};
